import { useField } from 'formik';
import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import { Grid, GridUnit } from '@hookdeck/theme';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import LINKS from '../../../../../configs/links';
import { fieldName } from '../../../../../utils';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Tabs from '../../../../common/base/Tabs';
import Text from '../../../../common/base/Text';
import DisplayDate from '../../../../common/DisplayDate';
import Dropdown from '../../../../common/Dropdown';
import EditorInput from '../../../../common/Form/Fields/EditorInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { ScreenModal } from '../../../../common/Modal';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import useEventList from '../../../../hooks/useEventList';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import Badge from '../../../../common/base/Badge';
import { FilterSection } from '../../../../../../../../typings/AICompletion.interface';
import FilterEditorInput from './FilterEditorInput';
import { FILTER_SECTIONS } from './useAIFilter';
const ApplyButtonWrapper = styled.div`
  visibility: hidden;
`;
const HoverableCardSection = styled(StyledCardSection)`
  &:hover ${ApplyButtonWrapper} {
    visibility: visible;
  }
`;
const test_input_default_values = {
  headers: '',
  body: '',
  query: '',
  path: ''
};
const FilterRuleForm: React.FC<{
  name: string;
  source_id?: string;
  webhook_id?: string;
  connection_name?: string;
  onClose: () => any;
}> = ({
  name,
  source_id,
  webhook_id,
  connection_name,
  onClose
}) => {
  const [{
    value: rule
  }] = useField(name);
  const [{
    value: test_input
  },, {
    setValue: setTestInput
  }] = useField(fieldName('test_input', name));
  const {
    addToast
  } = useToasts();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery<{
    filter_tab: string;
  }>();
  const active_tab = query.filter_tab || 'body';
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [test_results, setFilterTestResult] = useState<{
    headers: boolean;
    body: boolean;
    query: boolean;
    path: boolean;
  } | null>(null);
  const [selected_request_or_event_id, setRequestOrEventId] = useState<string>('');
  // The filter AI action is rendered in the "Filter schema" header.
  const [action_container, setActionContainer] = useState<HTMLDivElement | null>(null);
  const [{
    value: request_id_input
  }] = useField('request_id');
  const showEvents = !!webhook_id;
  const showRequests = !webhook_id;
  const {
    data: requests
  } = useSWR(source_id && showRequests && APIMethodKeys.requests.list({
    source_id,
    limit: 5
  }), () => HookdeckAPI.requests.list({
    source_id,
    limit: 5
  }));
  const {
    events
  } = useEventList(showEvents ? {
    webhook_id: [webhook_id],
    cli_id: {
      all: true
    }
  } : {}, {
    limit: 5,
    include_data: true,
    fetch: showEvents
  });
  const requests_or_events = showEvents ? events : requests?.models;
  const item_type = showEvents ? 'event' : 'request';
  const request_id = showRequests ? selected_request_or_event_id || requests?.models[0]?.id : null;
  const {
    data: request
  } = useSWR(request_id && showRequests && APIMethodKeys.requests.get(request_id), () => HookdeckAPI.requests.get(request_id!));
  const event_id = showEvents ? selected_request_or_event_id || events[0]?.id : undefined;
  const event_fetched = events.find(event => event.id === event_id);
  const {
    data: event_result
  } = useSWR(!!event_id && showEvents && !event_fetched && APIMethodKeys.events.get(event_id!), () => HookdeckAPI.events.get(event_id!));
  const event_0 = event_id ? event_fetched || event_result : events[0];
  const request_or_event = showEvents ? event_0 : request;
  const test_data = request_or_event?.data || test_input_default_values;
  const binary_test_data = request_or_event?.data?.body_format === 'binary';
  const sanitized_multipart_test_data = binary_test_data && typeof test_data.body === 'string';
  useEffect(() => {
    if (test_data) {
      const input = !test_data ? {
        headers: '"Data archived"',
        body: '"Data archived"',
        parsed_query: '"Data archived"',
        path: '"Data archived"'
      } : Object.keys(test_data).reduce((obj, key) => ({
        ...obj,
        [key]: JSON.stringify(test_data[key], null, '\t')
      }), {} as {
        [key: string]: string;
      });
      input.query = input.parsed_query;
      if (selected_request_or_event_id) {
        addToast('success', `Set ${item_type.toLowerCase()} data as filter input`);
      }
      setTestInput(input);
    }
    if (!test_data && !test_input) {
      setTestInput(test_input_default_values);
    }
  }, [test_data]);
  useEffect(() => {
    setFilterTestResult(null);
  }, [rule, test_input]);
  const onFilterTest = async ({
    headers,
    body,
    query: query_0,
    path
  }: {
    headers: any;
    body: any;
    query: any;
    path: any;
  }): Promise<void> => {
    let input_0: {
      headers: any;
      body: any;
      parsed_query: any;
      path: any;
    } | null = null;
    try {
      input_0 = {
        headers: test_input?.headers ? JSON.parse(test_input.headers) : null,
        body: test_input?.body ? JSON.parse(test_input?.body || '') : null,
        parsed_query: test_input?.query ? JSON.parse(test_input?.query || '') : null,
        path: test_input?.path ? JSON.parse(test_input?.path || '') : null
      };
    } catch {
      addToast('error', "Invalid JSON in input, can't test filter");
      return;
    }
    let filter: null | {
      headers: any;
      body: any;
      query: any;
      path: any;
    } = null;
    try {
      filter = {
        headers: headers ? JSON.parse(headers) : undefined,
        body: body ? JSON.parse(body) : undefined,
        query: query_0 ? JSON.parse(query_0) : undefined,
        path: path ? JSON.parse(path) : undefined
      };
    } catch {
      addToast('error', "Invalid JSON in filter syntax, can't test filter");
      return;
    }
    if (input_0 && filter) {
      return await HookdeckAPI.utils.testFilters(input_0, filter).then(results => setFilterTestResult(results[1]));
    }
  };
  const getSampleInput = async () => Object.fromEntries(FILTER_SECTIONS.filter(section => test_input?.[section]).map(section_0 => {
    try {
      return [section_0, JSON.parse(test_input[section_0])];
    } catch {
      return [section_0, test_input[section_0]];
    }
  }));
  const not_filtered = test_results && test_results.body && test_results.headers && test_results.query && test_results.path;
  return <ScreenModal>
      <Div p={{
      x: 6,
      y: 3
    }} flex={{
      align: 'center',
      justify: 'space-between'
    }}>
        <Div flex={{
        align: 'center',
        gap: 4
      }}>
          <Button onClick={onClose} outline icon="close" />
          <Div flex={{
          align: 'center',
          gap: 2
        }}>
            {connection_name && <>
                <Text semi muted>
                  {connection_name}
                </Text>
                <Text semi>/</Text>
              </>}
            <Text semi>Filter</Text>
          </Div>
        </Div>
        <Div flex={{
        align: 'center',
        gap: 3
      }}>
          {test_results && <>
              <Text semi flex={{
            align: 'center'
          }} m={{
            r: 2
          }}>
                <Icon left icon={not_filtered ? 'success' : 'block'} success={!!not_filtered} danger={!not_filtered} />
                {not_filtered ? 'Not Filtered' : 'Filtered'}
              </Text>
            </>}
          <Button onClick={() => onFilterTest(rule)} outline icon="rule">
            Test filter
          </Button>
          <Button primary icon="success" onClick={onClose}>
            Confirm changes
          </Button>
        </Div>
      </Div>
      <Divider />
      <Div p={{
      x: 6,
      t: 3
    }} flex={{
      align: 'center',
      justify: 'space-between'
    }}>
        <Tabs tabs={[{
        key: 'body',
        label: 'Body',
        icon: test_results ? test_results.body ? 'success' : 'block' : undefined,
        icon_theme: test_results ? test_results.body ? 'success' : 'danger' : undefined,
        ...(rule.body ? {
          badge_label: '1',
          badge_theme: test_results ? test_results.body === false ? 'danger' : 'success' : 'muted'
        } : {})
      }, {
        key: 'headers',
        label: 'Headers',
        icon: test_results ? test_results.headers ? 'success' : 'block' : undefined,
        icon_theme: test_results ? test_results.headers ? 'success' : 'danger' : undefined,
        ...(rule.headers ? {
          badge_label: '1',
          badge_theme: test_results ? test_results.headers === false ? 'danger' : 'success' : 'muted'
        } : {})
      }, {
        key: 'query',
        label: 'Query',
        icon: test_results ? test_results.query ? 'success' : 'block' : undefined,
        icon_theme: test_results ? test_results.query ? 'success' : 'danger' : undefined,
        ...(rule.query ? {
          badge_label: '1',
          badge_theme: test_results ? test_results.query === false ? 'danger' : 'success' : 'muted'
        } : {})
      }, {
        key: 'path',
        label: 'Path',
        icon: test_results ? test_results.path ? 'success' : 'block' : undefined,
        icon_theme: test_results ? test_results.path ? 'success' : 'danger' : undefined,
        ...(rule.path ? {
          badge_label: '1',
          badge_theme: test_results ? test_results.path === false ? 'danger' : 'success' : 'muted'
        } : {})
      }]} border={false} active_tab={active_tab} onTabSelected={key_0 => updateSearchQuery({
        filter_tab: key_0
      })} />
        <Button m={{
        b: 2
      }} ghost as="a" href={LINKS.product_docs.filters} target="_blank" icon="document">
          Filters syntax
        </Button>
      </Div>
      <Divider />

      {active_tab === 'body' && binary_test_data && <Div p={{
      x: 6,
      y: 3
    }}>
          <Text muted>
            {sanitized_multipart_test_data ? <>
                Filters evaluate the sanitized multipart string shown below. File contents and
                undecodable parts are omitted; the original bytes remain preserved.
              </> : <>
                This is a binary payload, so filters evaluate its body as <code>null</code>. The
                original bytes are preserved; header, query, and path filters remain available.
              </>}
          </Text>
        </Div>}

      <Grid>
        <GridUnit size={1 / 2}>
          <Div flex>
            <Div flex={{
            grow: true,
            direction: 'column'
          }} w={100} style={{
            height: 'calc(100vh - 102px)'
          }}>
              <Div p={{
              x: 4,
              y: 3,
              l: 6
            }} flex={{
              justify: 'space-between',
              align: 'center'
            }}>
                <Text semi>
                  {request_or_event ? `${selected_request_or_event_id || 'Latest'} ${item_type} data` : 'Test data'}
                </Text>
                <Dropdown ghost label="Change data" z_index={1080} expand_icon="chevron_down" icon="change">
                  <StyledCardSection p={4}>
                    <Text semi muted m={{
                    t: 0,
                    b: 2
                  }}>
                      Latest {item_type}
                    </Text>
                    <StyledCard overflow_hidden>
                      {requests_or_events && requests_or_events.length > 0 ? requests_or_events.sort((a, b) => new Date(a.created_at).getTime() > new Date(b.created_at).getTime() ? -1 : 1).map(request_or_event_0 => {
                      const is_event = !!request_or_event_0.request_id;
                      const is_success = is_event ? request_or_event_0.status === 'SUCCESSFUL' : !request_or_event_0.rejection_cause;
                      return <HoverableCardSection key={request_or_event_0.id} flex={{
                        align: 'center',
                        gap: 3,
                        justify: 'space-between'
                      }} p={3}>
                                <Div flex={{
                          align: 'center',
                          gap: 3
                        }}>
                                  <Badge danger={!is_success} success={is_success} subtle>
                                    {is_success ? is_event ? request_or_event_0.response_status : 'Accepted' : is_event ? request_or_event_0.response_status : 'Rejected'}
                                  </Badge>
                                  <Text mono muted flex={{
                            align: 'center',
                            gap: 1
                          }}>
                                    <DisplayDate key={is_event ? request_or_event_0.created_at : request_or_event_0.ingested_at} date={is_event ? request_or_event_0.created_at : request_or_event_0.ingested_at} />
                                    <span>·</span>
                                    {request_or_event_0.id}
                                  </Text>
                                </Div>
                                <ApplyButtonWrapper>
                                  <Button outline disabled={selected_request_or_event_id === request_or_event_0.id} m={{
                            y: -4,
                            r: -1.5
                          }} onClick={() => setRequestOrEventId(request_or_event_0.id)}>
                                    {selected_request_or_event_id === request_or_event_0.id ? 'Applied' : 'Apply'}
                                  </Button>
                                </ApplyButtonWrapper>
                              </HoverableCardSection>;
                    }) : <Text p={3} muted center>
                          No {item_type} found
                        </Text>}
                    </StyledCard>
                  </StyledCardSection>
                  <StyledCardSection p={4}>
                    <Text semi muted m={{
                    b: 2
                  }}>
                      Specific {item_type} ID
                    </Text>
                    <Div flex={{
                    align: 'center',
                    gap: 2
                  }}>
                      <TextInput w={100} m={{
                      b: 0
                    }} mono name={fieldName('request_id', name)} placeholder={`Enter a ${item_type} ID`} />
                      <Button outline large disabled={request_id_input === selected_request_or_event_id} onClick={() => setRequestOrEventId(rule.request_id)}>
                        Apply
                      </Button>
                    </Div>
                  </StyledCardSection>
                </Dropdown>
              </Div>
              <Divider />
              <EditorInput key={active_tab} flex={{
              direction: 'column',
              grow: true
            }} name={fieldName(`test_input.${active_tab}`, name)} />
            </Div>
            <Divider vertical />
          </Div>
        </GridUnit>
        <GridUnit size={1 / 2}>
          <Div flex={{
          grow: true,
          direction: 'column'
        }} style={{
          height: 'calc(100vh - 102px)'
        }}>
            <Div p={{
            x: 6,
            y: 3
          }} flex={{
            justify: 'space-between',
            align: 'center'
          }}>
              <Text semi>Filter schema</Text>
              {/* Same height as the "Change data" button, to align both headers */}
              <Div ref={setActionContainer} h={{
              px: 28
            }} flex={{
              align: 'center'
            }} />
            </Div>
            <Divider />
            <FilterEditorInput action_container={action_container} rule_name={name} section={active_tab as FilterSection} getSampleInput={getSampleInput} onGenerated={filters => {
            if (!filters[active_tab]) {
              const section_2 = FILTER_SECTIONS.find(section_1 => filters[section_1]);
              section_2 && updateSearchQuery({
                filter_tab: section_2
              });
            }
          }} />
          </Div>
        </GridUnit>
      </Grid>
    </ScreenModal>;
};
export default FilterRuleForm;